import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import styles from '../../assets/styles/YogaCourse/100Hours.module.css';
import Refund from 'components/Refund/refund';
import Hours24 from 'components/Hours24/Hours24';
import heroVideo from '../../assets/images/100hrs.webp';
import BooksSection from 'components/MyBooks/Bookssection';
import Form100 from './Form100';
import HowToReach from 'components/Homepage/HowToReach';
import OurCourses from 'components/OurCourses/OurCourses'; // ← updated component
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const API_BASE   = `${ENV_API_BASE_URL}/api`;
const ASSET_BASE = `${ENV_API_BASE_URL}`;

const toAssetUrl = (value) => {
  if (!value) return '';
  if (typeof value !== 'string') return value;
  if (value.startsWith('http') || value.startsWith('data:')) return value;
  return `${ASSET_BASE}/${value.replace(/^\/+/, '')}`;
};

// ── Static defaults ───────────────────────────────────────────────────────────

const DEFAULT_HERO = {
  quoteText: 'Transform Your Practice, Inspire Others, Become a Certified Yoga Teacher',
  title: 'Yoga Teacher Training in Rishikesh, India',
  breadcrumbLabel: 'Yoga Training',
  bannerImage: '',
};
const DEFAULT_OVERVIEW = {
  tag: 'Course Overview',
  title: 'A Comprehensive, Residential Program Designed for Beginners and Practitioners',
  description:
    'This yoga teacher training program is your gateway to authentic yoga practice in the birthplace of yoga. This transformative journey in Rishikesh combines traditional teachings with modern methodology, covering essential aspects of yoga philosophy, anatomy, pranayama, meditation, and asanas.',
};
const DEFAULT_INFO_CARDS = [
  { title: 'Duration',      tag: 'Course Duration', detail: '📅 Intensive residential program',          icon: '📅' },
  { title: 'Level',         tag: 'All Levels',       detail: '📊 Suitable for all levels',               icon: '📊' },
  { title: 'Certification', tag: 'Certification',    detail: '🏆 Internationally recognized certificate', icon: '🏆' },
  { title: 'Location',      tag: 'Rishikesh, India', detail: '📍 Yoga capital of the world',             icon: '📍' },
];
const DEFAULT_WHY = {
  tag: 'Why Rishikesh?',
  title: 'Discover the Magic of Learning Yoga in Its Birthplace',
  description: 'Rishikesh is the yoga capital of the world, home to authentic yoga schools and ancient wisdom.',
  items: [
    { icon: '🏔️', title: 'Birthplace of Yoga',  description: 'Rishikesh is the yoga capital of the world, home to authentic yoga schools and ancient wisdom.' },
    { icon: '🕉️', title: 'Divine Energy',        description: 'Located in the Himalayas with the holy Ganges flowing through, creating awe-inspiring spiritual energy.' },
    { icon: '🧘‍♂️', title: 'Authentic Learning', description: 'Learn from experienced masters who carry forward the traditional Himalayan yoga lineage.' },
    { icon: '🌿', title: 'Natural Setting',       description: 'Peaceful environment surrounded by mountains and nature, perfect for deep yoga practice.' },
  ],
};
const DEFAULT_CURRICULUM = {
  tag: "What You'll Learn",
  title: 'Comprehensive Curriculum Covering All Aspects of Yoga',
  items: [
    { category: 'Hatha Yog',        items: ['140+ traditional asanas', 'Surya & Chandra Namaskar', 'Standing, sitting, prone & supine postures', 'Balancing & twisting postures'] },
    { category: 'Ashtanga Vinyasa', items: ['Primary series practice', 'Sun salutations A & B', 'Standing & sitting sequences', 'Finishing postures'] },
    { category: 'Pranayama',        items: ['Nadishodhana', 'Kapalbhati', 'Bhastrika', 'Ujjayi, Bhramari, Sheetali'] },
    { category: 'Meditation',       items: ['Himalayan meditation', 'Breath awareness', 'Mantra meditation', 'Yoga Nidra'] },
    { category: 'Philosophy',       items: ['Yoga Sutras of Patanjali', '8 limbs of Yoga', 'Bhagavad Gita wisdom', 'Chakras & Nadis'] },
    { category: 'Anatomy',          items: ['Skeletal & muscular system', 'Respiratory & circulatory system', 'Alignment principles', 'Injury prevention'] },
  ],
};
const DEFAULT_SCHEDULE = {
  tag: 'Daily Schedule',
  title: 'A Typical Day in Your Yoga Teacher Training Journey',
  items: [
    { time: '6:00 AM',  activity: 'Morning Tea' },
    { time: '6:30 AM',  activity: 'Pranayama & Meditation' },
    { time: '8:00 AM',  activity: 'Hatha Yog Asana Practice' },
    { time: '10:00 AM', activity: 'Breakfast' },
    { time: '11:00 AM', activity: 'Yoga Philosophy & Ancient Texts' },
    { time: '12:30 PM', activity: 'Anatomy & Alignment' },
    { time: '1:30 PM',  activity: 'Lunch' },
    { time: '3:00 PM',  activity: 'Ashtanga Vinyasa Practice' },
    { time: '5:00 PM',  activity: 'Teaching Methodology' },
    { time: '6:30 PM',  activity: 'Dinner' },
    { time: '7:30 PM',  activity: 'Satsang / Self Study / Kirtan' },
  ],
};
const DEFAULT_FEATURES = {
  tag: 'Course Features',
  title: 'Everything You Need for a Transformative Yoga Journey',
  items: [
    { icon: '🧘',   title: 'Expert Teachers',         desc: '15-30+ years of experience in traditional yoga' },
    { icon: '🏔️',  title: 'Rishikesh Setting',        desc: 'Yoga capital of the world' },
    { icon: '📜',   title: 'Yoga Alliance',            desc: 'Internationally certified courses' },
    { icon: '🌿',   title: 'Organic Meals',            desc: 'Nutritious sattvic vegetarian food' },
    { icon: '🏠',   title: 'Accommodation',            desc: 'Comfortable & clean rooms with modern facilities' },
    { icon: '🧘‍♀️', title: 'Small Groups',            desc: 'Personal attention guaranteed' },
    { icon: '📚',   title: 'Comprehensive Curriculum', desc: 'All aspects of yoga covered' },
    { icon: '🎓',   title: 'Certification',            desc: 'Internationally recognized' },
  ],
};
const DEFAULT_INCLUDED_SECTION = {
  tag: "What's Included",
  title: 'Everything You Need for a Comfortable Stay and Learning Experience',
  includedTitle: 'What does the course fee include?',
  notIncludedTitle: 'What is not included?',
  toBringTitle: 'What to bring with you?',
  items: [
    '11-12 nights accommodation (shared/private)',
    '3 nutritious vegetarian meals daily',
    'Yoga materials, books & manuals',
    'Weekend excursions & sightseeing',
    'Kirtan nights & cultural programs',
    'Cleansing kit (Jala neti, rubber neti)',
    'Fire ceremony & Ganga Aarti',
    '24/7 WiFi access',
    'Laundry service',
    'Free pickup from Dehradun Airport',
  ],
  notIncluded: [],
  toBringImage: '',
};
const DEFAULT_TESTIMONIALS = {
  tag: 'What Our Students Say',
  title: 'Hear from Those Who Experienced the Transformation',
  items: [
    { name: 'Sarah Johnson', country: 'USA',    text: 'This course transformed my life. The teachers are incredibly knowledgeable, and the setting in Rishikesh is magical. I left feeling confident and prepared to continue my yoga journey.' },
    { name: 'Michael Chen',  country: 'Canada', text: 'The perfect introduction to yoga teacher training. Small class sizes meant I got personal attention, and the curriculum was comprehensive yet accessible for beginners.' },
    { name: 'Emma Williams', country: 'UK',     text: 'The spiritual atmosphere of Rishikesh combined with expert teaching made this an unforgettable experience. The daily practice by the Ganges was life-changing.' },
  ],
};
const DEFAULT_FAQS = {
  tag: 'Frequently Asked Questions',
  title: 'Get Answers to Common Questions',
  items: [
    { q: 'Which airport should I fly to?',         a: 'The closest airport is New Delhi (DEL). From there, you can take a domestic flight to Dehradun (DED) which is 30 minutes from Rishikesh, or travel by taxi/bus (6-7 hours).' },
    { q: 'Do I need prior yoga experience?',        a: 'No prior experience is required. The course is designed for beginners and those looking to deepen their practice. At least 6 months of practice is recommended but not mandatory.' },
    { q: 'Will I get certification?',               a: 'You will receive a 100-hour certificate. To become a certified yoga teacher (RYT-200), you need to complete the remaining 100 hours within one year at the same school.' },
    { q: 'What is included in the course fee?',     a: 'Accommodation, 3 meals daily, course materials, excursions, WiFi, and airport pickup from Dehradun are all included. AC/heater may cost extra.' },
    { q: 'Is the food suitable for special diets?', a: 'Yes! We provide vegetarian, vegan, and gluten-free options. Please inform us of any allergies or dietary requirements in advance.' },
  ],
};
const DEFAULT_ACCOMMODATION = {
  tag: 'Accommodation',
  title: 'Experience a Comfortable Stay in Our Peaceful Ashram',
  image: '',
  rooms: [
    { type: 'Shared Rooms',  description: 'Twin-sharing with attached bathroom, hot water' },
    { type: 'Private Rooms', description: 'Single occupancy with attached bathroom' },
  ],
  features: [
    { label: 'Room Features', detail: 'Comfortable beds, storage, balcony/views' },
    { label: 'Facilities',    detail: '24/7 hot water, Wi-Fi, daily housekeeping' },
    { label: 'Environment',   detail: 'Serene setting near the Ganges, surrounded by nature' },
  ],
};
const DEFAULT_FOOD = {
  tag: 'Food',
  title: 'We Serve Fresh, Organic, Sattvic Vegetarian Meals',
  image: '',
  meals: [
    { meal: 'Breakfast',     description: 'Energizing and light, seasonal fruits, porridge, tea' },
    { meal: 'Lunch',         description: 'Wholesome Indian thali, dal, vegetables, rice, chapati' },
    { meal: 'Dinner',        description: 'Nourishing and easy to digest, soups, vegetables' },
    { meal: 'Dietary Needs', description: 'Vegan and gluten-free options available on request' },
  ],
};
const DEFAULT_WHY_CHOOSE = {
  tag: 'Why Choose This Course?',
  title: 'This Course is Ideal For You',
  image: '',
  idealForTitle: 'This course is ideal for:',
  idealFor: [
    'Beginners starting their yoga journey',
    'Practitioners wanting to build a strong foundation',
    'Aspiring Teachers preparing for 200-hour certification',
    'Time-conscious students who want to split their training',
    'Self-discovery seekers looking for personal transformation',
  ],
  benefitsTitle: 'Key Benefits:',
  benefits: [
    'Learn authentic yoga in its birthplace',
    'Build a strong foundation in Hatha and Ashtanga yoga',
    'Understand basic anatomy, philosophy, and teaching methodology',
    'Complete first half of 200-hour certification',
    'Flexible completion—finish remaining 100 hours within 1 year',
    'Immerse yourself in the spiritual energy of Rishikesh',
  ],
};
const DEFAULT_CTA = {
  title: 'Ready to Begin Your Journey?',
  subtitle: 'Transform your life through yoga. Join us in Rishikesh for an unforgettable experience.',
  primaryLabel: 'Apply Now',   primaryLink: '/BookingForm',
  secondaryLabel: 'Contact Us', secondaryLink: '/contact-us',
};
const DEFAULT_OUR_COURSES_SECTION = {
  tag: 'Our Courses',
  title: 'Our Courses',
  description: '',
};

// ── Helper ────────────────────────────────────────────────────────────────────
const mergeWithDefault = (dbValue, defaultValue) => {
  if (!dbValue) return defaultValue;
  const merged = { ...defaultValue };
  Object.keys(defaultValue).forEach((key) => {
    const db  = dbValue[key];
    const def = defaultValue[key];
    if (Array.isArray(def))           merged[key] = Array.isArray(db) && db.length > 0 ? db : def;
    else if (typeof def === 'string') merged[key] = db && String(db).trim() ? db : def;
    else                              merged[key] = db !== undefined && db !== null ? db : def;
  });
  return merged;
};

// ─────────────────────────────────────────────────────────────────────────────

export default function YogaCourse100({ courseSlugOverride }) {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [course, setCourse] = useState(null);

  const courseSlug = courseSlugOverride || slug || '100-hour-yttc';

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const res  = await axios.get(`${API_BASE}/courses/slug/${courseSlug}`);
        setCourse(res.data?.data || null);
      } catch (error) {
        console.error('Failed to load course data', error);
      }
    };
    fetchCourse();
  }, [courseSlug]);

  const content = course?.content || {};

  // ── content.* merges ──────────────────────────────────────────────────────
  const hero = { ...DEFAULT_HERO, ...(content.hero || {}) };
  if (!content.hero?.title && course?.title) hero.title = course.title;
  if (!content.hero?.breadcrumbLabel && (course?.shortTitle || course?.title))
    hero.breadcrumbLabel = course?.shortTitle || course?.title;

  const overview = { ...DEFAULT_OVERVIEW, ...(content.overview || {}) };
  if (!content.overview?.description && course?.description)
    overview.description = course.description;

  const fallbackInfoCards = useMemo(() => {
    const duration      = course?.duration || course?.teacherTraining?.duration;
    const level         = course?.level;
    const certification = course?.teacherTraining?.certification || course?.home?.certification;
    const location      = course?.location;
    return [
      { ...DEFAULT_INFO_CARDS[0], tag: duration     || DEFAULT_INFO_CARDS[0].tag, detail: duration     ? `📅 ${duration}`     : DEFAULT_INFO_CARDS[0].detail },
      { ...DEFAULT_INFO_CARDS[1], tag: level         || DEFAULT_INFO_CARDS[1].tag, detail: level         ? `📊 ${level}`        : DEFAULT_INFO_CARDS[1].detail },
      { ...DEFAULT_INFO_CARDS[2], tag: certification || DEFAULT_INFO_CARDS[2].tag, detail: certification ? `🏆 ${certification}` : DEFAULT_INFO_CARDS[2].detail },
      { ...DEFAULT_INFO_CARDS[3], tag: location      || DEFAULT_INFO_CARDS[3].tag, detail: location      ? `📍 ${location}`     : DEFAULT_INFO_CARDS[3].detail },
    ];
  }, [course]);

  const infoCards           = content.infoCards?.length ? content.infoCards : fallbackInfoCards;
  const whySection          = { ...DEFAULT_WHY,             ...(content.whyRishikesh || {}) };
  const curriculumSection   = { ...DEFAULT_CURRICULUM,       ...(content.curriculum   || {}) };
  const scheduleSection     = { ...DEFAULT_SCHEDULE,         ...(content.dailySchedule || {}) };
  const featuresSection     = { ...DEFAULT_FEATURES,         ...(content.features     || {}) };
  const includedSection     = { ...DEFAULT_INCLUDED_SECTION, ...(content.included     || {}) };
  const testimonialsSection = { ...DEFAULT_TESTIMONIALS,     ...(content.testimonials || {}) };
  const faqSection          = { ...DEFAULT_FAQS,             ...(content.faqs         || {}) };
  const ctaSection          = { ...DEFAULT_CTA,              ...(content.cta          || {}) };

  // ── Top-level dynamic sections ────────────────────────────────────────────
  const accommodationSection = mergeWithDefault(
    course?.accommodation && Object.keys(course.accommodation).length
      ? course.accommodation : content.accommodation,
    DEFAULT_ACCOMMODATION
  );
  const foodSection = mergeWithDefault(
    course?.food && Object.keys(course.food).length
      ? course.food : content.food,
    DEFAULT_FOOD
  );
  const whyChooseSection = mergeWithDefault(
    course?.whyChoose && Object.keys(course.whyChoose).length
      ? course.whyChoose : content.whyChoose,
    DEFAULT_WHY_CHOOSE
  );
  const ourCoursesSection = mergeWithDefault(
    course?.ourCoursesSection && Object.keys(course.ourCoursesSection).length
      ? course.ourCoursesSection : null,
    DEFAULT_OUR_COURSES_SECTION
  );

  const heroImage        = toAssetUrl(hero.bannerImage || course?.banner || course?.card?.image) || heroVideo;
  const includedItems    = includedSection.items?.length ? includedSection.items : DEFAULT_INCLUDED_SECTION.items;
  const notIncludedItems = includedSection.notIncluded || [];
  const courseTitle      = hero.title || course?.title || DEFAULT_HERO.title;

  return (
    <>
      {/* Banner */}
      <div className={styles.bannerWrapper}>
        <img src={heroImage} alt={courseTitle} className={styles.bannerImage} />
      </div>

      {/* Hero */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <p className={styles.quoteText}>{hero.quoteText}</p>
          <h1 className={styles.mainTitle}>{hero.title}</h1>
          <div className={styles.breadcrumb}>
            <span className={styles.breadcrumbLink}>Home</span>
            <span className={styles.breadcrumbSeparator}>/</span>
            <span>{hero.breadcrumbLabel}</span>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className={styles.overviewSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{overview.tag}</p>
          <h2 className={styles.sectionTitle}>{overview.title}</h2>
          <div className={styles.decorativeLine}></div>
          <p className={styles.sectionDescription}>{overview.description}</p>
        </div>
        <div className={styles.infoGrid}>
          {infoCards.map((card, index) => (
            <div className={styles.infoCard} key={index}>
              <div className={styles.infoContent}>
                <h3 className={styles.infoCardTitle}>{card.title}</h3>
                <div className={styles.infoTags}><span className={styles.infoTag}>{card.tag}</span></div>
                <div className={styles.infoDetails}><p>{card.detail}</p></div>
              </div>
              <div className={styles.infoImageSection}>
                <div className={styles.infoIcon}>{card.icon}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Form100
        courseId={course?._id}
        courseTitle={courseTitle}
        batches={course?.batches || []}
        included={includedItems}
        notIncluded={notIncludedItems}
        toBringImage={includedSection.toBringImage}
        includedTitle={includedSection.includedTitle}
        notIncludedTitle={includedSection.notIncludedTitle}
        toBringTitle={includedSection.toBringTitle}
      />

      {/* Why Rishikesh */}
      <section className={styles.whyRishikeshSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{whySection.tag}</p>
          <h2 className={styles.sectionTitle}>{whySection.title}</h2>
          <div className={styles.decorativeLine}></div>
          <p className={styles.sectionDescription}>{whySection.description}</p>
        </div>
        <div className={styles.contentGrid}>
          {(whySection.items || DEFAULT_WHY.items).map((item, index) => (
            <div key={index} className={styles.contentCard}>
              <div className={styles.contentInfo}>
                <h3 className={styles.contentCardTitle}>{item.title}</h3>
                <div className={styles.contentTags}><span className={styles.contentTag}>{item.icon}</span></div>
                <div className={styles.contentDescription}><p>{item.description}</p></div>
              </div>
              <div className={styles.contentIconSection}>
                <div className={styles.largeIcon}>{item.icon}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Curriculum */}
      <section className={styles.curriculumSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{curriculumSection.tag}</p>
          <h2 className={styles.sectionTitle}>{curriculumSection.title}</h2>
          <div className={styles.decorativeLine}></div>
        </div>
        <div className={styles.curriculumGrid}>
          {(curriculumSection.items || DEFAULT_CURRICULUM.items).map((item, index) => (
            <div key={index} className={styles.curriculumCard}>
              <div className={styles.curriculumInfo}>
                <h3 className={styles.curriculumTitle}>{item.category}</h3>
                <div className={styles.curriculumContent}>
                  <ul className={styles.curriculumList}>
                    {item.items.map((point, idx) => <li key={idx}>{point}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Daily Schedule */}
      <section className={styles.scheduleSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{scheduleSection.tag}</p>
          <h2 className={styles.sectionTitle}>{scheduleSection.title}</h2>
          <div className={styles.decorativeLine}></div>
        </div>
        <div className={styles.scheduleGrid}>
          {(scheduleSection.items || DEFAULT_SCHEDULE.items).map((item, index) => (
            <div key={index} className={styles.scheduleCard}>
              <div className={styles.scheduleInfo}>
                <h3 className={styles.scheduleTime}>{item.time}</h3>
                <div className={styles.scheduleActivity}><p>{item.activity}</p></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className={styles.FeaturesSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{featuresSection.tag}</p>
          <h2 className={styles.sectionTitle}>{featuresSection.title}</h2>
          <div className={styles.decorativeLine}></div>
        </div>
        <div className={styles.featuresGrid}>
          {(featuresSection.items || DEFAULT_FEATURES.items).map((feature, index) => (
            <div key={index} className={styles.featureCard}>
              <div className={styles.featureInfo}>
                <h3 className={styles.featureTitle}>{feature.title}</h3>
                <div className={styles.featureTags}><span className={styles.featureTag}>{feature.icon}</span></div>
                <div className={styles.featureDescription}><p>{feature.desc}</p></div>
              </div>
              <div className={styles.featureIconSection}>
                <div className={styles.featureIcon}>{feature.icon}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Included */}
      <section className={styles.includedSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{includedSection.tag}</p>
          <h2 className={styles.sectionTitle}>{includedSection.title}</h2>
          <div className={styles.decorativeLine}></div>
        </div>
        <div className={styles.includedGrid}>
          {includedItems.map((item, index) => (
            <div key={index} className={styles.includedCard}>
              <div className={styles.includedInfo}>
                <div className={styles.includedIcon}>✓</div>
                <p className={styles.includedText}>{item}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Hours24 />

      {/* Testimonials */}
      <section className={styles.testimonialsSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{testimonialsSection.tag}</p>
          <h2 className={styles.sectionTitle}>{testimonialsSection.title}</h2>
          <div className={styles.decorativeLine}></div>
        </div>
        <div className={styles.testimonialsGrid}>
          {(testimonialsSection.items || DEFAULT_TESTIMONIALS.items).map((testimonial, index) => (
            <div key={index} className={styles.testimonialCard}>
              <div className={styles.testimonialInfo}>
                <h3 className={styles.testimonialName}>{testimonial.name}</h3>
                <div className={styles.testimonialTags}><span className={styles.testimonialTag}>{testimonial.country}</span></div>
                <div className={styles.testimonialText}><p>{testimonial.text}</p></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Refund />

      {/* Accommodation */}
      <section className={styles.accommodationSection}>
        <div className={styles.accomSplit}>
          <div className={styles.accomImagePanel}>
            {accommodationSection.image ? (
              <img src={toAssetUrl(accommodationSection.image)} alt="Accommodation" className={styles.accomImage} />
            ) : (
              <div className={styles.accomImagePlaceholder}>
                <span className={styles.accomPlaceholderText}>Accommodation</span>
              </div>
            )}
          </div>
          <div className={styles.accomContentPanel}>
            <p className={styles.sectionTag}>{accommodationSection.tag}</p>
            <h2 className={styles.sectionTitle}>{accommodationSection.title}</h2>
            <div className={styles.decorativeLine}></div>
            {accommodationSection.rooms?.length > 0 && (
              <div className={styles.accomRoomTypes}>
                {accommodationSection.rooms.map((room, index) => (
                  <div key={index} className={styles.accomRoomRow}>
                    <h3 className={styles.accomRoomType}>{room.type}</h3>
                    <p className={styles.accomRoomDesc}>{room.description}</p>
                  </div>
                ))}
              </div>
            )}
            {accommodationSection.features?.length > 0 && (
              <div className={styles.accomFeaturesList}>
                {accommodationSection.features.map((feature, index) => (
                  <div key={index} className={styles.accomFeatureRow}>
                    <span className={styles.accomFeatureLabel}>{feature.label}:</span>
                    <span className={styles.accomFeatureDetail}>{feature.detail}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Food */}
      <section className={styles.foodSection}>
        <div className={styles.foodSplit}>
          <div className={styles.foodContentPanel}>
            <p className={styles.sectionTag}>{foodSection.tag}</p>
            <h2 className={styles.sectionTitle}>{foodSection.title}</h2>
            <div className={styles.decorativeLine}></div>
            {foodSection.meals?.length > 0 && (
              <div className={styles.foodMealsList}>
                {foodSection.meals.map((item, index) => (
                  <div key={index} className={styles.foodMealRow}>
                    <div className={styles.foodMealHeader}>
                      <span className={styles.foodMealIndex}>{String(index + 1).padStart(2, '0')}</span>
                      <h3 className={styles.foodMealTitle}>{item.meal}</h3>
                    </div>
                    <p className={styles.foodMealDesc}>{item.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className={styles.foodImagePanel}>
            {foodSection.image ? (
              <img src={toAssetUrl(foodSection.image)} alt="Food" className={styles.foodImage} />
            ) : (
              <div className={styles.foodImagePlaceholder}>
                <span className={styles.foodPlaceholderText}>Sattvic Meals</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className={styles.whyChooseSection}>
        <div className={styles.whyChooseSplit}>
          <div className={styles.whyChooseImagePanel}>
            {whyChooseSection.image ? (
              <img src={toAssetUrl(whyChooseSection.image)} alt="Why Choose" className={styles.whyChooseImage} />
            ) : (
              <div className={styles.whyChooseImagePlaceholder}>
                <span className={styles.whyChoosePlaceholderText}>Your Journey Begins Here</span>
              </div>
            )}
          </div>
          <div className={styles.whyChooseContentPanel}>
            <p className={styles.sectionTag}>{whyChooseSection.tag}</p>
            <h2 className={styles.sectionTitle}>{whyChooseSection.title}</h2>
            <div className={styles.decorativeLine}></div>
            {whyChooseSection.idealFor?.length > 0 && (
              <div className={styles.whyChooseBlock}>
                <h3 className={styles.whyChooseBlockTitle}>{whyChooseSection.idealForTitle}</h3>
                {whyChooseSection.idealFor.map((item, index) => (
                  <div key={index} className={styles.whyChooseRow}>
                    <span className={styles.whyChooseNum}>{String(index + 1).padStart(2, '0')}</span>
                    <span className={styles.whyChooseText}>{item}</span>
                  </div>
                ))}
              </div>
            )}
            {whyChooseSection.benefits?.length > 0 && (
              <div className={styles.whyChooseBlock}>
                <h3 className={styles.whyChooseBlockTitle}>{whyChooseSection.benefitsTitle}</h3>
                {whyChooseSection.benefits.map((item, index) => (
                  <div key={index} className={styles.whyChooseRow}>
                    <span className={styles.whyChooseTick}>✓</span>
                    <span className={styles.whyChooseText}>{item}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className={styles.faqSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{faqSection.tag}</p>
          <h2 className={styles.sectionTitle}>{faqSection.title}</h2>
          <div className={styles.decorativeLine}></div>
        </div>
        <div className={styles.faqGrid}>
          {(faqSection.items || DEFAULT_FAQS.items).map((faq, index) => (
            <div key={index} className={styles.faqCard}>
              <div className={styles.faqInfo}>
                <h3 className={styles.faqQuestion}>{faq.q}</h3>
                <div className={styles.faqAnswer}><p>{faq.a}</p></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Our Courses — uses the OurCourses component with dynamic props ── */}
      <OurCourses
        courseId={course?._id}
        category={course?.category}
        excludeSlug={courseSlug}
        sectionData={ourCoursesSection}
      />

      <BooksSection />
      <HowToReach />

      {/* CTA */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>{ctaSection.title}</h2>
          <p className={styles.ctaSubtitle}>{ctaSection.subtitle}</p>
          <div className={styles.ctaButtons}>
            <button
              className={styles.ctaButton}
              onClick={() => navigate(ctaSection.primaryLink || '/BookingForm')}
            >
              {ctaSection.primaryLabel}
            </button>
            <button
              className={`${styles.ctaButton} ${styles.ctaButtonSecondary}`}
              onClick={() => navigate(ctaSection.secondaryLink || '/contact-us')}
            >
              {ctaSection.secondaryLabel}
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
